import { COLORS } from '@btime/constants'
import { concatIf } from '@btime/helpers'
import { useFilter } from '@btime/react-contexts'
import { useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Col, Grid } from '../../../packages/kiti/components/structure/Grid'
import { Title } from '../../../components/presentation/Text'
import { useNavigation } from '../../../utils/contexts/NavigationContext'
import {
  useOpenEditFieldsModal,
  useOpenExportHistoryModal,
  useOpenExportModal,
  useOpenImportHistoryModal,
  useOpenRoutinesModal,
  useOpenPublicTokensModal,
  useOpenSemaphoreConfigModal,
  useOpenTemplatesModal,
  useOpenTriggersModal,
  useOpenServiceOrderAutomaticDistributionListModal,
  useOpenSlaConfigModal,
  useOpenTagsModal
} from '../../../utils/routes/modals/modalsActions'
import DropdownLink from '../../../components/navigation/DropdownLink'
import IconText from '../../../components/presentation/IconText'
import Separator from '../../../packages/kiti/components/helpers/Separator'

const PageHeader = () => {
  const openTriggersModal = useOpenTriggersModal()
  const openEditFieldsModal = useOpenEditFieldsModal()
  const openImportHistoryModal = useOpenImportHistoryModal()
  const openExportModal = useOpenExportModal()
  const openExportHistoryModal = useOpenExportHistoryModal()
  const openTemplatesModal = useOpenTemplatesModal()
  const openSemaphoreConfigModal = useOpenSemaphoreConfigModal()
  const openRoutinesModal = useOpenRoutinesModal()
  const openServiceOrderAutomaticDistributionListModal = useOpenServiceOrderAutomaticDistributionListModal()
  const openSlaConfigModal = useOpenSlaConfigModal()
  const openTagsModal = useOpenTagsModal()

  const filter = useFilter()

  const { t } = useTranslation(['common', 'listServiceOrders', 'listPlaces'])
  const navigate = useNavigation()

  const canListTriggers = useSessionPermission('listTrigger')
  const canListRoutines = useSessionPermission('listRoutine')
  const canExport = useSessionPermission('exportServiceOrder')
  const canImport = useSessionPermission('importServiceOrder')
  const canSetup = useSessionPermission('setupServiceOrder')
  const canListTemplate = useSessionPermission('listTemplate')
  const canListPublicToken = useSessionPermission('listPublicToken')
  const canUseAutomaticDistribution = useSessionPermission('useAutomaticDistribution')
  const canCreateSlaToServiceOrder = useSessionPermission('canCreateSlaToServiceOrder')
  const canSetupServiceOrderTag = useSessionPermission('setupServiceOrderTag')
  const openPublicTokensModal = useOpenPublicTokensModal()

  const options = [
    ...concatIf(canListTriggers, [
      {
        value: 'triggers',
        iconLeft: 'RiRobotLine',
        onClick: () =>
          openTriggersModal({
            entities: [
              'service_order',
              'event',
              'reply',
              'service_order_reply',
              'service_order_tag_assigned',
              'service_order_tag_removed'
            ]
          })
      },

      {
        value: 'integrations',
        iconLeft: 'RiPlugLine',
        onClick: () => openTriggersModal({ entities: ['service_order', 'event', 'reply'], integrationsOnly: true })
      },

      { line: true }
    ]),
    ...concatIf(canListRoutines, [
      {
        value: 'routines',
        iconLeft: 'RiCalendar2Line',
        onClick: () => openRoutinesModal({ entities: ['service_order'] })
      },
      { line: true }
    ]),

    ...concatIf(canListTemplate, [
      {
        value: 'templates',
        iconLeft: 'RiFileCopy2Line',
        onClick: () => openTemplatesModal('service_order')
      },

      { line: true }
    ]),

    ...concatIf(canImport, [
      {
        value: 'import',
        iconLeft: 'RiUploadCloud2Line',
        onClick: () => navigate(`/import/v2/serviceOrder`)
      },
      {
        value: 'importHistory',
        iconLeft: 'RiTimeLine',
        onClick: () => openImportHistoryModal('serviceOrder')
      },

      { line: true }
    ]),

    ...concatIf(canExport, [
      {
        value: 'export',
        iconLeft: 'RiDownloadCloud2Line',
        onClick: () => openExportModal({ entity: 'serviceOrder', filter })
      },

      {
        value: 'exportHistory',
        iconLeft: 'RiTimeLine',
        onClick: () => openExportHistoryModal('serviceOrder')
      },
      { line: true }
    ]),

    ...concatIf(canListPublicToken, [
      {
        value: 'publicToken',
        iconLeft: 'BsReply',
        onClick: () => openPublicTokensModal('service_order')
      },

      { line: true }
    ]),

    ...concatIf(canCreateSlaToServiceOrder, [
      {
        value: 'sla',
        iconLeft: 'RiTimerLine',
        onClick: () => openSlaConfigModal({ name: 'service_order_sla' })
      }
    ]),

    ...concatIf(canSetup, [
      {
        value: 'semaphore',
        iconLeft: 'RiAlarmWarningLine',
        onClick: () => openSemaphoreConfigModal({ name: 'service_order_started_since_colors' })
      },
      {
        value: 'fields',
        iconLeft: 'RiListSettingsLine',
        onClick: () => openEditFieldsModal('service_orders')
      }
    ]),
    ...concatIf(canSetupServiceOrderTag, [
      {
        value: 'tags',
        iconLeft: 'RiPriceTag3Line',
        onClick: () => openTagsModal()
      }
    ]),
    ...concatIf(canUseAutomaticDistribution, [
      {
        value: 'configurations.automatic.distribution',
        iconLeft: 'RiUserSettingsLine',
        onClick: () => openServiceOrderAutomaticDistributionListModal({ name: 'automatic_service_orders_distribution' })
      }
    ])
  ]

  return (
    <>
      <Grid justify="space-between" align="center" gutter={0.8}>
        <Col>
          <Title size="h2" color={COLORS.dark3} noWrap>
            {t('listServiceOrders:title')}
          </Title>
        </Col>

        {/* TODO: Take it back after migrate intercom articles to another place */}
        {/* <Col>
          <PageGuide />
        </Col> */}
        <Col flex={1}></Col>

        {!!options.length && (
          <Col>
            <DropdownLink
              fromRight
              t={key => t(`listServiceOrders:menu.${key}`)}
              options={options}
              actionContent={
                <IconText label={t('common:moreOptions')} iconName="BsThreeDotsVertical" noWrap size="small" bold />
              }
            />
          </Col>
        )}
      </Grid>
      <Separator weight={2} />
    </>
  )
}

export default PageHeader
